/**
 * Aarthika Pro — Internationalization (i18n)
 *
 * Supports: English, Hindi, Gujarati, Marathi, Tamil
 * Usage: t('key') returns translated string
 */

export type Language = 'en' | 'hi' | 'gu' | 'mr' | 'ta';

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    'nav.company': 'Company',
    'nav.data': 'Data',
    'nav.quickAccess': 'Quick Access',
    'nav.gst': 'GST',
    'nav.import': 'Import',
    'nav.export': 'Export',
    'nav.print': 'Print',

    // Gateway
    'gateway.businessOverview': 'Business Overview',
    'gateway.currentPeriod': 'Current Period',
    'gateway.currentDate': 'Current Date',
    'gateway.nameOfCompany': 'Name of Company',
    'gateway.dateOfLastEntry': 'Date of Last Entry',

    // Masters
    'master.creation': 'Master Creation',
    'master.alteration': 'Master Alteration',
    'master.accountsList': 'Accounts List',
    'master.group': 'Group',
    'master.ledger': 'Ledger',
    'master.currency': 'Currency',
    'master.voucherType': 'Voucher Type',
    'master.stockGroup': 'Stock Group',
    'master.stockItem': 'Stock Item',
    'master.unit': 'Unit',
    'master.location': 'Location',
    'master.employee': 'Employee',

    // Transactions
    'txn.vouchers': 'Vouchers',
    'txn.quotations': 'Quotations',
    'txn.dayBook': 'Day Book',
    'txn.ledgers': 'Ledgers',

    // Voucher Types
    'voucher.sales': 'Sales',
    'voucher.purchase': 'Purchase',
    'voucher.receipt': 'Receipt',
    'voucher.payment': 'Payment',
    'voucher.journal': 'Journal',
    'voucher.contra': 'Contra',
    'voucher.creditNote': 'Credit Note',
    'voucher.debitNote': 'Debit Note',

    // Reports
    'report.trialBalance': 'Trial Balance',
    'report.balanceSheet': 'Balance Sheet',
    'report.profitLoss': 'Profit & Loss',
    'report.cashFlow': 'Cash Flow',
    'report.fundsFlow': 'Funds Flow',
    'report.ratioAnalysis': 'Ratio Analysis',
    'report.stockSummary': 'Stock Summary',
    'report.bankReconciliation': 'Bank Reconciliation',

    // Dashboard
    'dash.totalSales': 'Total Sales',
    'dash.totalPurchase': 'Total Purchase',
    'dash.cashBalance': 'Cash Balance',
    'dash.bankBalance': 'Bank Balance',
    'dash.receivables': 'Receivables',
    'dash.payables': 'Payables',
    'dash.netProfit': 'Net Profit',
    'dash.netLoss': 'Net Loss',
    'dash.booksHealth': 'Books Health',
    'dash.trialBalance': 'Trial Balance',
    'dash.balanced': 'Balanced',
    'dash.liquidCash': 'Liquid Cash',
    'dash.pendingDues': 'Pending Dues',
    'dash.allClear': 'All clear',
    'dash.activity': 'Activity',
    'dash.today': 'Today',

    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.create': 'Create',
    'common.accept': 'Accept',
    'common.quit': 'Quit',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.loading': 'Loading...',
    'common.total': 'Total',
    'common.amount': 'Amount',
    'common.date': 'Date',
    'common.name': 'Name',
    'common.address': 'Address',
    'common.mobile': 'Mobile',
    'common.email': 'Email',
    'common.parties': 'parties',

    // Settings
    'settings.appearance': 'Appearance',
    'settings.language': 'Language',
    'settings.eyeCare': 'Eye Care Mode',
    'settings.eyeCare.off': 'Off',
    'settings.eyeCare.warm': 'Warm Light',
    'settings.eyeCare.sepia': 'Sepia',
    'settings.eyeCare.highContrast': 'High Contrast',
  },

  hi: {
    'nav.company': 'कंपनी',
    'nav.data': 'डेटा',
    'nav.quickAccess': 'त्वरित एक्सेस',
    'nav.gst': 'जीएसटी',
    'nav.import': 'आयात',
    'nav.export': 'निर्यात',
    'nav.print': 'प्रिंट',
    'gateway.businessOverview': 'व्यापार अवलोकन',
    'gateway.currentPeriod': 'वर्तमान अवधि',
    'gateway.currentDate': 'वर्तमान तिथि',
    'gateway.nameOfCompany': 'कंपनी का नाम',
    'gateway.dateOfLastEntry': 'अंतिम प्रविष्टि की तिथि',
    'master.creation': 'मास्टर निर्माण',
    'master.alteration': 'मास्टर संशोधन',
    'master.accountsList': 'खाता सूची',
    'master.group': 'समूह',
    'master.ledger': 'खाता',
    'master.currency': 'मुद्रा',
    'master.voucherType': 'वाउचर प्रकार',
    'master.stockGroup': 'स्टॉक समूह',
    'master.stockItem': 'स्टॉक आइटम',
    'master.unit': 'इकाई',
    'master.location': 'स्थान',
    'master.employee': 'कर्मचारी',
    'txn.vouchers': 'वाउचर',
    'txn.quotations': 'कोटेशन',
    'txn.dayBook': 'दिनपुस्तिका',
    'txn.ledgers': 'खाते',
    'voucher.sales': 'बिक्री',
    'voucher.purchase': 'खरीद',
    'voucher.receipt': 'रसीद',
    'voucher.payment': 'भुगतान',
    'voucher.journal': 'जर्नल',
    'voucher.contra': 'कॉन्ट्रा',
    'voucher.creditNote': 'क्रेडिट नोट',
    'voucher.debitNote': 'डेबिट नोट',
    'report.trialBalance': 'तलपट',
    'report.balanceSheet': 'तुलन पत्र',
    'report.profitLoss': 'लाभ और हानि',
    'report.cashFlow': 'नकद प्रवाह',
    'report.ratioAnalysis': 'अनुपात विश्लेषण',
    'report.stockSummary': 'स्टॉक सारांश',
    'report.bankReconciliation': 'बैंक समाधान',
    'dash.totalSales': 'कुल बिक्री',
    'dash.totalPurchase': 'कुल खरीद',
    'dash.cashBalance': 'नकद शेष',
    'dash.bankBalance': 'बैंक शेष',
    'dash.receivables': 'प्राप्तियाँ',
    'dash.payables': 'देय राशि',
    'dash.netProfit': 'शुद्ध लाभ',
    'dash.netLoss': 'शुद्ध हानि',
    'dash.booksHealth': 'बहीखाता स्वास्थ्य',
    'dash.trialBalance': 'तलपट',
    'dash.balanced': 'संतुलित',
    'dash.liquidCash': 'तरल नकद',
    'dash.pendingDues': 'बकाया राशि',
    'dash.allClear': 'सब ठीक',
    'dash.activity': 'गतिविधि',
    'dash.today': 'आज',
    'common.save': 'सहेजें',
    'common.cancel': 'रद्द करें',
    'common.delete': 'हटाएं',
    'common.create': 'बनाएं',
    'common.accept': 'स्वीकार करें',
    'common.quit': 'बाहर',
    'common.yes': 'हाँ',
    'common.no': 'नहीं',
    'common.loading': 'लोड हो रहा है...',
    'common.total': 'कुल',
    'common.amount': 'राशि',
    'common.date': 'तिथि',
    'common.name': 'नाम',
    'common.address': 'पता',
    'common.mobile': 'मोबाइल',
    'common.email': 'ईमेल',
    'common.parties': 'पार्टियाँ',
    'settings.appearance': 'दिखावट',
    'settings.language': 'भाषा',
    'settings.eyeCare': 'आँखों की सुरक्षा',
    'settings.eyeCare.off': 'बंद',
    'settings.eyeCare.warm': 'गर्म प्रकाश',
    'settings.eyeCare.sepia': 'सेपिया',
    'settings.eyeCare.highContrast': 'उच्च कॉन्ट्रास्ट',
  },

  gu: {
    'nav.company': 'કંપની',
    'nav.data': 'ડેટા',
    'nav.quickAccess': 'ઝડપી ઍક્સેસ',
    'nav.gst': 'જીએસટી',
    'gateway.businessOverview': 'વ્યાપાર ઓવરવ્યુ',
    'gateway.currentPeriod': 'ચાલુ સમયગાળો',
    'gateway.currentDate': 'આજની તારીખ',
    'gateway.nameOfCompany': 'કંપનીનું નામ',
    'gateway.dateOfLastEntry': 'છેલ્લી એન્ટ્રીની તારીખ',
    'master.creation': 'માસ્ટર બનાવો',
    'master.ledger': 'ખાતું',
    'master.group': 'ગ્રુપ',
    'voucher.sales': 'વેચાણ',
    'voucher.purchase': 'ખરીદી',
    'voucher.receipt': 'રસીદ',
    'voucher.payment': 'ચુકવણી',
    'dash.totalSales': 'કુલ વેચાણ',
    'dash.totalPurchase': 'કુલ ખરીદી',
    'dash.cashBalance': 'રોકડ બેલેન્સ',
    'dash.bankBalance': 'બેંક બેલેન્સ',
    'dash.receivables': 'લેણાં',
    'dash.payables': 'દેણાં',
    'dash.netProfit': 'ચોખ્ખો નફો',
    'dash.netLoss': 'ચોખ્ખું નુકસાન',
    'common.save': 'સાચવો',
    'common.cancel': 'રદ કરો',
    'common.yes': 'હા',
    'common.no': 'ના',
    'common.total': 'કુલ',
    'common.amount': 'રકમ',
    'common.date': 'તારીખ',
    'settings.eyeCare': 'આંખોની સુરક્ષા',
    'settings.eyeCare.off': 'બંધ',
    'settings.eyeCare.warm': 'ગરમ પ્રકાશ',
  },

  mr: {
    'nav.company': 'कंपनी',
    'nav.data': 'डेटा',
    'gateway.businessOverview': 'व्यवसाय आढावा',
    'gateway.currentPeriod': 'चालू कालावधी',
    'gateway.currentDate': 'आजची तारीख',
    'gateway.nameOfCompany': 'कंपनीचे नाव',
    'master.creation': 'मास्टर तयार करा',
    'master.ledger': 'खातेवही',
    'master.group': 'गट',
    'voucher.sales': 'विक्री',
    'voucher.purchase': 'खरेदी',
    'voucher.receipt': 'पावती',
    'voucher.payment': 'देयक',
    'dash.totalSales': 'एकूण विक्री',
    'dash.totalPurchase': 'एकूण खरेदी',
    'dash.cashBalance': 'रोख शिल्लक',
    'dash.bankBalance': 'बँक शिल्लक',
    'dash.receivables': 'येणे',
    'dash.payables': 'देणे',
    'dash.netProfit': 'निव्वळ नफा',
    'dash.netLoss': 'निव्वळ तोटा',
    'common.save': 'जतन करा',
    'common.cancel': 'रद्द करा',
    'common.yes': 'होय',
    'common.no': 'नाही',
    'common.total': 'एकूण',
    'common.amount': 'रक्कम',
    'settings.eyeCare': 'डोळ्यांची काळजी',
    'settings.eyeCare.off': 'बंद',
    'settings.eyeCare.warm': 'उबदार प्रकाश',
  },

  ta: {
    'nav.company': 'நிறுவனம்',
    'nav.data': 'தரவு',
    'gateway.businessOverview': 'வணிக கண்ணோட்டம்',
    'gateway.currentPeriod': 'தற்போதைய காலம்',
    'gateway.currentDate': 'இன்றைய தேதி',
    'gateway.nameOfCompany': 'நிறுவனத்தின் பெயர்',
    'master.creation': 'மாஸ்டர் உருவாக்கம்',
    'master.ledger': 'பேரேடு',
    'master.group': 'குழு',
    'voucher.sales': 'விற்பனை',
    'voucher.purchase': 'கொள்முதல்',
    'voucher.receipt': 'ரசீது',
    'voucher.payment': 'செலுத்தல்',
    'dash.totalSales': 'மொத்த விற்பனை',
    'dash.totalPurchase': 'மொத்த கொள்முதல்',
    'dash.cashBalance': 'பண இருப்பு',
    'dash.bankBalance': 'வங்கி இருப்பு',
    'dash.receivables': 'பெறத்தக்கவை',
    'dash.payables': 'செலுத்தத்தக்கவை',
    'dash.netProfit': 'நிகர லாபம்',
    'dash.netLoss': 'நிகர நஷ்டம்',
    'common.save': 'சேமி',
    'common.cancel': 'ரத்து',
    'common.yes': 'ஆம்',
    'common.no': 'இல்லை',
    'common.total': 'மொத்தம்',
    'common.amount': 'தொகை',
    'settings.eyeCare': 'கண் பாதுகாப்பு',
    'settings.eyeCare.off': 'ஆஃப்',
    'settings.eyeCare.warm': 'சூடான ஒளி',
  },
};

let currentLanguage: Language = 'en';

export function getLanguage(): Language {
  return currentLanguage;
}

export function t(key: string): string {
  return translations[currentLanguage]?.[key] || translations['en']?.[key] || key;
}

// React hook for language — triggers re-render on change
let listeners: Set<() => void> = new Set();

export function onLanguageChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setLanguage(lang: Language): void {
  currentLanguage = lang;
  document.documentElement.setAttribute('data-lang', lang);
  listeners.forEach(fn => fn());
}

export function getAvailableLanguages(): { code: Language; name: string; nativeName: string }[] {
  return [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिंदी' },
    { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
    { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
    { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  ];
}
